import { GripVertical, MoreVertical, X, Maximize2, RotateCcw, RefreshCw, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { PlatformBadge } from "./PlatformBadge";
import { useDashboardDataSourceSafe } from "@/contexts/DashboardDataSourceContext";

type WidgetType = "kpi" | "chart" | "table";

interface DashboardWidgetProps {
  title: string;
  children: React.ReactNode;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onRemove?: () => void;
  onResize?: () => void;
  onResetSize?: () => void;
  isEditMode?: boolean;
  className?: string;
  showPlatformBadge?: boolean;
  platformSlug?: string;
  widgetType?: WidgetType;
}

function KpiSkeleton() {
  return (
    <div className="p-4 flex flex-col justify-center h-full gap-3">
      <div className="h-3 bg-gray-100 rounded animate-pulse w-1/3" />
      <div className="h-8 bg-gray-100 rounded animate-pulse w-2/3" />
    </div>
  );
}

function ChartSkeleton() {
  return (
    <div className="p-4 flex flex-col h-full">
      <div className="flex-1 bg-gray-100 rounded animate-pulse" />
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="p-4 space-y-0">
      <div className="flex gap-3 py-2 border-b border-gray-100">
        <div className="h-3 bg-gray-100 rounded animate-pulse w-24" />
        <div className="h-3 bg-gray-100 rounded animate-pulse w-16" />
        <div className="h-3 bg-gray-100 rounded animate-pulse w-16" />
        <div className="h-3 bg-gray-100 rounded animate-pulse w-12" />
      </div>
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex gap-3 py-2.5 border-b border-gray-50">
          <div className="h-3 bg-gray-50 rounded animate-pulse w-24" />
          <div className="h-3 bg-gray-50 rounded animate-pulse w-16" />
          <div className="h-3 bg-gray-50 rounded animate-pulse w-16" />
          <div className="h-3 bg-gray-50 rounded animate-pulse w-12" />
        </div>
      ))}
    </div>
  );
}

function LoadingSkeleton({ widgetType }: { widgetType: WidgetType }) {
  if (widgetType === "kpi") return <KpiSkeleton />;
  if (widgetType === "chart") return <ChartSkeleton />;
  return <TableSkeleton />;
}

export function DashboardWidget({
  title,
  children,
  isLoading = false,
  error = null,
  onRetry,
  onRemove,
  onResize,
  onResetSize,
  isEditMode = false,
  className,
  showPlatformBadge = true,
  platformSlug,
  widgetType = "chart",
}: DashboardWidgetProps) {
  const dataSource = useDashboardDataSourceSafe();
  const ctxPlatforms = dataSource?.platformSlugs ?? [];
  const resolvedPlatform = platformSlug ?? (ctxPlatforms.length === 1 ? ctxPlatforms[0] : null);

  return (
    <div
      className={cn(
        "analytics-widget rounded-xl h-full flex flex-col overflow-hidden",
        isEditMode ? "border-dashed border-2" : "",
        className
      )}
      style={{
        background: "#FFFFFF",
        border: isEditMode ? "2px dashed var(--analytics-chart-soft, #d8a4b8)" : "1px solid #DCE3ED",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      }}
      data-widget-type={widgetType}
      data-testid={`widget-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="analytics-widget-header flex items-center justify-between px-4 py-3 flex-shrink-0" style={{ borderBottom: "1px solid #DCE3ED" }}>
        <div className="flex items-center gap-2 min-w-0">
          {isEditMode && (
            <div className="drag-handle cursor-grab active:cursor-grabbing flex-shrink-0" style={{ color: "#94A3B8" }}>
              <GripVertical className="h-4 w-4" />
            </div>
          )}
          <h3 className="text-sm font-semibold truncate" style={{ color: "#6B6B6B" }}>{title}</h3>
          {showPlatformBadge && resolvedPlatform && (
            <PlatformBadge platform={resolvedPlatform} size="sm" variant="muted" />
          )}
          {showPlatformBadge && !resolvedPlatform && (
            <div className="flex-shrink-0 w-5 h-5 rounded bg-primary/5 flex items-center justify-center" title="All Platforms">
              <Layers className="h-3 w-3 text-primary" />
            </div>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-gray-600">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-36">
            {onResize && (
              <DropdownMenuItem onClick={onResize}>
                <Maximize2 className="h-3.5 w-3.5 mr-2" />
                Resize
              </DropdownMenuItem>
            )}
            {onResetSize && (
              <DropdownMenuItem onClick={onResetSize}>
                <RotateCcw className="h-3.5 w-3.5 mr-2" />
                Reset Size
              </DropdownMenuItem>
            )}
            {onRemove && (
              <DropdownMenuItem onClick={onRemove} className="text-red-600 focus:text-red-600">
                <X className="h-3.5 w-3.5 mr-2" />
                Remove
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="analytics-widget-body flex-1 min-h-0 overflow-auto">
        {isLoading ? (
          <LoadingSkeleton widgetType={widgetType} />
        ) : error ? (
          <div className="flex flex-col items-center justify-center p-6 text-center h-full rounded-b-xl" style={{ background: "#F7F9FC" }}>
            <div className="w-10 h-10 rounded-full flex items-center justify-center mb-3" style={{ background: "#DCE3ED" }}>
              <RefreshCw className="h-4 w-4" style={{ color: "#5F6D82" }} />
            </div>
            <p className="text-sm mb-1" style={{ color: "#5F6D82" }}>Unable to load data</p>
            <p className="text-xs mb-4 max-w-[200px]" style={{ color: "#94A3B8" }}>{error}</p>
            {onRetry && (
              <Button variant="outline" size="sm" onClick={onRetry} className="text-xs bg-white hover:bg-gray-100 text-gray-600 border-gray-200">
                <RefreshCw className="h-3 w-3 mr-1" />
                Retry
              </Button>
            )}
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}
